import { AlkLogger, IAlkListMenuOption } from '@alk/core';
import { BLOTTER_TAB, ERightSideBarEnum, SETTINGS_OPTION } from '../enums';
import {
    CLICK_MODE_ARRAY, COLUMN_STATE_PROPS, IColumnState, IFilterModel,
    ILimit,
    IPadConfig,
    ISettings,
    ITradingQuantities,
    PAD_CONFIG_REQUIRED_PROPS,
} from '../interfaces';
import { ensurerObjectType } from './object-utils';
import { EnumNumberLike, RequiredKeys } from '../types';
import { PropertyParser, TransformAction } from '../interfaces/functions.interface';

export const undefinedIntegerParser: TransformAction<string, number> =
    (val: string): number | undefined =>
        Number.isInteger(Number(val)) ? Number(val) : undefined;

export const enumParser = <T>(enumLike: EnumNumberLike, fallback: T): TransformAction<string, T> =>
    (val: string): T => {
        const parsedVal = JSON.parse(val);
        const match = Object.values(enumLike).find(value => value === parsedVal);
        return (match ?? fallback) as T;
    };

export const arrayParser = <T>(transformator: TransformAction<unknown, T | undefined>) => (serializedStr: string): Array<T> => {
    try {
        const parsedArray = JSON.parse(serializedStr);

        if (Array.isArray(parsedArray)) {
            return parsedArray
                .map(item => {
                    const result = transformator(item);

                    if (!Boolean(result)){
                        AlkLogger.error(`[ArrayParser] object '${ JSON.stringify(item) }' not parsed`);
                    }

                    return result;
                })
                .filter(Boolean);
        }

        AlkLogger.error(`[ArrayParser] serialized object '${ serializedStr }' not parsed to array of items`);

        return [];
    } catch (err) {
        AlkLogger.error(
            `[ArrayParser] serialized object '${ serializedStr }' not parsed to array of items`,
            err,
        );
    }
    return [];
};

export const objectParser = <T>(props: ReadonlyArray<RequiredKeys<T>>) => (obj: unknown): T | undefined =>
    ensurerObjectType<T>(obj, props) ? obj : undefined;

export const transformatorObject = <T>(transformator: TransformAction<unknown, T | undefined>) => (serializedStr: string): T => {
    try {

        const result = transformator(serializedStr);
        if (!Boolean(result)){
            AlkLogger.error(`[transformatorObject] serialized object '${ JSON.stringify(serializedStr) }' not parsed`);
            return undefined;
        }
        return JSON.parse(serializedStr);

    } catch {
        AlkLogger.error(`[transformatorObject] serialized object '${ serializedStr }' not parsed as object.`);
        return undefined;
    }
};

export const stringParser = (obj: unknown): string | undefined =>
    typeof obj === 'string' ? obj : undefined;

export const transformatorStringOrObject = <T>(transformator: TransformAction<unknown, T | undefined>) => (serializedStr: string): T => {
    try {

        const result = transformator(serializedStr);
        if (!Boolean(result)){
            console.log('!Boolean(result)');
            AlkLogger.error(`[transformatorStringOrObject] serialized element '${ JSON.stringify(serializedStr) }' not parsed`);
            return undefined;
        }
        return JSON.parse(serializedStr);

    } catch {
        AlkLogger.error(`[transformatorStringOrObject] serialized element '${ serializedStr }' not parsed.`);
        return undefined;
    }
};

export const booleanParser = (fallbackValue: boolean) => (value: string): boolean => {
    try {
        const parsedValue = JSON.parse(value);

        if (typeof parsedValue === 'boolean') {
            return parsedValue;
        } else {
            AlkLogger.error(`[booleanParser] serialized object '${ value }' not parsed as boolean. ${ ''
            }Used fallback value ${ fallbackValue }`);
            return fallbackValue;
        }

    } catch {
        AlkLogger.error(`[booleanParser] serialized object '${ value }' not parsed as boolean. ${ ''
        }Used fallback value ${ fallbackValue }`);
        return fallbackValue;
    }
};

export const literalParser = <T extends string>(allValues: ReadonlyArray<T>, fallback: T) => (value: string): T => {
    try {
        const sanitizedValue = JSON.parse(value);

        if ((allValues as ReadonlyArray<string>).includes(sanitizedValue)) {
            return sanitizedValue as unknown as T;
        } else {
            AlkLogger.error(`[literalParser] serialized object '${ value }' not as one of: ${ allValues }. ${ ''
            }Used fallback value ${ fallback }`);
            return fallback;
        }
    } catch {
        AlkLogger.error(`[literalParser] serialized object '${ value }' not as one of: ${ allValues }. ${ ''
        }Used fallback value ${ fallback }`);
        return fallback;
    }
};

export const StateObjectTransformationCollection: PropertyParser<ISettings, string> = {
    selectedClientId: undefinedIntegerParser,
    pads: (item) => arrayParser(objectParser<IPadConfig>(PAD_CONFIG_REQUIRED_PROPS))(item),
    selectedRightBarIdx: enumParser<ERightSideBarEnum>(ERightSideBarEnum, ERightSideBarEnum.NONE),
    tradingLayoutHeight: undefinedIntegerParser,
    readNotifications: (item) => arrayParser(stringParser)(item),
    clickMode: literalParser(CLICK_MODE_ARRAY, 'confirm'),
    tradingEnabled: booleanParser(false),
    spotColumnState: (item) => arrayParser(objectParser<IColumnState>(COLUMN_STATE_PROPS))(item),
    forwardColumnState: (item) => arrayParser(objectParser<IColumnState>(COLUMN_STATE_PROPS))(item),
    pendingColumnState: (item) => arrayParser(objectParser<IColumnState>(COLUMN_STATE_PROPS))(item),
    kondorColumnState: (item) => arrayParser(objectParser<IColumnState>(COLUMN_STATE_PROPS))(item),
    positionColumnState: (item) => arrayParser(objectParser<IColumnState>(COLUMN_STATE_PROPS))(item),
    spotFilterModel: (item) => transformatorObject(objectParser<IFilterModel>([]))(item),
    forwardFilterModel: (item) => transformatorObject(objectParser<IFilterModel>([]))(item),
    pendingFilterModel: (item) => transformatorObject(objectParser<IFilterModel>([]))(item),
    isColumnFilteringEnabled: booleanParser(false),
    blotterSearchFilter: transformatorStringOrObject(stringParser),
    blotterSelectedTab: literalParser(BLOTTER_TAB, 'spot'),
    selectedNotificationFilter: arrayParser(objectParser<IAlkListMenuOption>([])),
    selectedSetting: literalParser(SETTINGS_OPTION, 'click-and-confirm'),
    tradingQuantities: (item) => transformatorObject(objectParser<ITradingQuantities>([]))(item),
    selectedLimit: (item) => transformatorObject(objectParser<ILimit>([]))(item),
};
